import {EventResponse} from "../../data/events/EventResponse";
import {Button, Chip, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow} from "@mui/material";
import React from "react";
import {formatTimestamp, pluralize} from "../../utils";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PreviewIcon from '@mui/icons-material/Preview';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import useUser, {UserContextProps} from "../../hooks/useUser";
import {makeApiCall} from "../../hooks/CancellableApiCall";
import EditEventForm from "./EditEventForm";

interface EventListProps {
    events: EventResponse[]
    preloadedEventType?: string
    canCreateEvents: boolean
}

interface EventListRowProps {
    event: EventResponse,
    user: UserContextProps
}

const EventListRow = (props: EventListRowProps) => {
    let lengthInMinutes = props.event.length / 60
    const navigate = useNavigate()
    const user = useUser()

    let rejoinableEventAttendeeStatus: string[] = ["cancelled", "invited"]

    let userAttendee = props.event.eventAttendees.find(T => {
        return T.communityId === props.user.user?.communityId
    })

    let eventUser = props.event.eventAttendees.find((attendee) => {
        return attendee.communityId === props.user.user?.communityId
    })

    let closedEventTypes: string[] = ["ended", "cancelled"]

    let statusChip

    switch (eventUser?.status) {
        case "attending":
            statusChip = <Chip size="small" label={"Attending"} color={"primary"} />
            break
        case "attended":
            statusChip = <Chip size="small" label={"Attended"} color={"success"} />
            break
        case "host":
            statusChip = <Chip size="small" label={"Host"} color={"secondary"} />
            break
        case "did_not_attend":
            statusChip = <Chip size="small" label={"Missed"} color={"error"} />
            break
        case "invited":
            statusChip = <Chip size="small" label={"Invited"} color={"warning"} />
            break
        case "cancelled":
            statusChip = <Chip size="small" label={"Cancelled"} color={"warning"} />
            break
        default:
            statusChip = <Chip size="small" label={"Not Joined"} sx={{backgroundColor: "darkgray"}} />
    }

    let privateChip = <>
    <Chip size="small" label={props.event.private ? "Private" : "Public"} color={props.event.private ? "error" : "success"} sx={{ml: 0.5}}/>
    </>

    return <TableRow>
        <TableCell>{formatTimestamp(props.event.date)}</TableCell>
        <TableCell>{props.event.type.displayName} {privateChip}</TableCell>
        <TableCell>{lengthInMinutes} {pluralize("Minute", lengthInMinutes)} </TableCell>
        <TableCell><Link underline="none" component={RouterLink} to={`/user/${props.event.host.communityId}`}>
            {props.event.host.nick}
        </Link></TableCell>
        <TableCell>{props.event.status[0].toUpperCase() + props.event.status.slice(1)}</TableCell>
        <TableCell>{statusChip}</TableCell>
        <TableCell>
            <Stack spacing={2} direction={"row"}>
                <Button size={"medium"} sx={{maxWidth: "100px"}} color="info" variant="contained" onClick={() => {
                    navigate(`/event/view/${props.event.id}`)
                }}>
                    View<PreviewIcon/>
                </Button>
                {
                    userAttendee === undefined || rejoinableEventAttendeeStatus.includes(userAttendee.status) ? <>
                        <Button disabled={closedEventTypes.includes(props.event.status)}
                                size={"medium"} sx={{maxWidth: "100px"}} color="info" variant="contained" onClick={() => {
                            makeApiCall({
                                url: `/api/event/own/status`,
                                method: "PATCH",
                                body: {
                                    eventId: props.event.id,
                                    newStatus: "ATTENDING"
                                },
                                onLoadedCallback: () => {
                                    navigate(`/event/view/${props.event.id}`)
                                },
                                onError: () => {}
                            })
                        }}>
                            Attend<EmojiPeopleIcon/>
                        </Button>
                    </> : <>
                        <Button size={"medium"} disabled={
                            (props.event.host.communityId === props.user.user?.communityId) || (closedEventTypes.includes(props.event.status))
                        }
                                sx={{maxWidth: "160px"}} color="warning" variant="contained" onClick={() => {
                            makeApiCall({
                                url: `/api/event/own/status`,
                                method: "PATCH",
                                body: {
                                    eventId: props.event.id,
                                    newStatus: "CANCELLED"
                                },
                                onLoadedCallback: () => {
                                    navigate(`/event/view/${props.event.id}`)
                                },
                                onError: () => {}
                            })
                        }}>
                            Withdraw<EmojiPeopleIcon/>
                        </Button>
                    </>
                }
                        {(props.event.host.communityId === props.user.user?.communityId || user.hasPermissions("events:admin")) && (!["started", "ended"].includes(props.event.status)) && (
                             <EditEventForm id={props.event.id}
                                            date={props.event.date}
                                            length={props.event.length}
                                            description={props.event.description}
                                            private={props.event.private}
                                            status={props.event.status}
                                />
                            )}
            </Stack>
        </TableCell>
    </TableRow>
}

export const EventList = (props: EventListProps) => {
    const navigate = useNavigate()
    const user = useUser()

    return <Stack spacing={2} sx={{overflowX: "auto"}}>
        {
            props.canCreateEvents ? <>
                <Button size={"medium"} sx={{maxWidth: "200px"}} color="success" variant="contained" onClick={() => {
                    navigate(`/event/create/${props.preloadedEventType ?? ""}`)
                }}>
                    New Event<CalendarMonthIcon/>
                </Button>
            </> : <></>
        }
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Event Type</TableCell>
                    <TableCell>Length</TableCell>
                    <TableCell>Host</TableCell>
                    <TableCell>Event Status</TableCell>
                    <TableCell>Your Status</TableCell>
                    <TableCell>Event Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {props.events?.map((event, idx) => {
                    return <EventListRow key={idx} user={user} event={event}/>
                })}
            </TableBody>
        </Table>
    </Stack>
}
